import { useCallback, useEffect, useState } from 'react';
import { refusalWords } from '../i18n/refusal';
import { useOneAtATime } from './useOneAtATime';
import type { ReportedPostView, SiteLogEntry, SuspendedReaderView } from '@versesverse/core';
import type { Strings } from '../i18n/strings';
import { readableTime } from './sessionTime';
import { useLang } from '../i18n/lang';

/** What the reports queue reads off the social client. */
export interface ReportsClient {
  reportedPosts(): Promise<{ waiting: number; posts: ReportedPostView[] }>;
  /** Take it down — the server accepts a moderator on any post, not only its author. */
  deletePost(postId: string): Promise<void>;
  /** Looked at, nothing wrong with it. Without this the only move is to remove. */
  clearReports(postId: string): Promise<void>;
  /** The kill switch: ends their session on the next request, and disconnects them now. */
  suspendReader(userId: string): Promise<void>;
  /** Let them back in — nothing else can find a suspended reader, so this list is the way. */
  suspendedReaders(options?: { beforeId?: string }): Promise<SuspendedReaderView[]>;
  /** What site moderators have DONE — the history the list above is a single frame of. */
  siteLog(options?: { before?: string; beforeId?: string }): Promise<SiteLogEntry[]>;
  reinstateReader(userId: string): Promise<void>;
}

export interface ReportedPostsProps {
  client: ReportsClient;
  /** The signed-in reader's role. Anything but a site moderator renders nothing at all. */
  role: string | null;
  /** Names a node, so a moderator can see WHERE something was written. Falls back to the id. */
  nodeName(nodeId: string): string;
  /** Go to the passage the post is on, to read it in context before judging it. */
  onGoToNode(nodeId: string): void;
}

function moderates(role: string | null): boolean {
  return role === 'moderator' || role === 'admin';
}

/*
 * WHAT HAS BEEN REPORTED — and until now, nowhere.
 *
 * Reporting a post incremented a counter that **no route read and no page showed**. A reader who
 * saw something ugly tapped Report, was told "Reported", and that was the end of it: the server has
 * always let a moderator remove any post, so the power to act existed and nothing could tell anyone
 * what to act on. For a product whose subject is where traditions disagree, the one control a
 * reader has against abuse ended in a number in a document.
 *
 * NOTHING AT ALL FOR EVERYBODY ELSE. It is a list of accusations about named people, so it is drawn
 * only for whoever the server would answer — and asked for only then, so an ordinary reader's page
 * makes no request that would 403.
 *
 * Two verbs, and the second is the one that makes it a queue rather than a bin: without "nothing
 * wrong with it" a post reported out of malice or plain disagreement either comes down or waits for
 * ever, and a moderator's only tool is the heaviest one.
 */
/**
 * WHAT A SITE-LOG ROW SAYS — a lookup, because the action comes off the WIRE.
 *
 * This was a ternary on , which is exhaustive over the type and not over the
 * data: an act this bundle has never heard of rendered as **"reinstated by"**, which is a false
 * sentence about a moderator in the record kept so that a second moderator can supervise them.
 * A browser holding a cached app is reading a server that has since learnt a new act — twice this
 * session — and the honest answer names what is actually known: who, to whom, and when.
 *
 * Its own vocabulary rather than the Hall log's, because the grammar of the row is different:
 * the name and the actor have spans of their own, so the phrase here is the verb between them.
 */
const SITE_SAYS: Partial<Record<string, keyof Strings>> = {
  readerSuspended: 'reports.wasSuspended',
  readerReinstated: 'reports.wasReinstated',
};

/** The verb between the two names, or the honest answer for an act this bundle cannot name. */
export function siteSaidBy(action: string): keyof Strings {
  return SITE_SAYS[action] ?? 'reports.wasSomething';
}

export function ReportedPosts({ client, role, nodeName, onGoToNode }: ReportedPostsProps): React.JSX.Element | null {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [waiting, setWaiting] = useState(0);
  const [posts, setPosts] = useState<ReportedPostView[]>([]);
  /** Who is suspended — the list that makes the kill switch reversible by anyone but an operator. */
  const [suspended, setSuspended] = useState<SuspendedReaderView[]>([]);
  /** What was done, and by whom — the history the list above has none of. */
  const [history, setHistory] = useState<SiteLogEntry[]>([]);
  /** Whether the walk back has reached the end of the record — see `earlier`. */
  const [historyEnded, setHistoryEnded] = useState(false);
  /* And the same for the suspended list, which is a page too — see `moreSuspended` below. */
  const [suspendedEnded, setSuspendedEnded] = useState(false);
  /* What a press did, when it did not do it — see `acting` below. */
  const [problem, setProblem] = useState<string | null>(null);
  /**
   * WHETHER ANY OF THE THREE READS FAILED — and this panel is where that mattered most.
   *
   * All three caught their failure and set an EMPTY value: no reports, nobody suspended, no
   * history. The count in the heading then read `(0)` and the panel below decided there was nothing
   * to show — so a moderator whose reads had failed was told the queue was clear, and if all three
   * failed the queue **disappeared entirely**. Not "nothing waiting": no queue at all, and nothing
   * anywhere saying why.
   *
   * It escaped the sweep that catches this family because that one asks the dictionary which
   * PHRASES claim emptiness, and this panel claims it with a number.
   */
  const [unread, setUnread] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    const answer = await client.reportedPosts();
    setWaiting(answer.waiting);
    setPosts(answer.posts);
  }, [client]);

  /*
   * THE DESK, READ — a named read rather than an effect body, because two things ask for it now:
   * arriving, and coming back to a tab that was left open.
   *
   * `stillHere` is the arriving caller's guard, handed in: a moderator who stops moderating (or a
   * role that arrives late) must not be shown an answer read for the person they were.
   */
  const readDesk = useCallback((stillHere: () => boolean = () => true): void => {
    if (!moderates(role)) { setPosts([]); setWaiting(0); setSuspended([]); setHistory([]); return; }
    setUnread(false);
    client.reportedPosts()
      .then(answer => { if (stillHere()) { setWaiting(answer.waiting); setPosts(answer.posts); } })
      .catch(() => { if (stillHere()) { setWaiting(0); setPosts([]); setUnread(true); } });
    /* Asked for at the same time, because a suspension nobody can see is one nobody will lift. */
    client.suspendedReaders()
      .then(readers => { if (stillHere()) setSuspended(readers); })
      .catch(() => { if (stillHere()) { setSuspended([]); setUnread(true); } });
    /* And what was DONE, because the list above is one bit of state with no history behind it: who
       suspended them, when, and how many times this reader has been through it. */
    client.siteLog()
      .then(entries => { if (stillHere()) setHistory(entries); })
      .catch(() => { if (stillHere()) { setHistory([]); setUnread(true); } });
  }, [client, role]);

  useEffect(() => {
    let live = true;
    readDesk(() => live);
    return () => { live = false; };
  }, [readDesk]);

  /*
   * AND AGAIN WHEN THE MODERATOR COMES BACK TO THE TAB.
   *
   * The same criterion as the rooms panel and the Hall's door: this queue is a list that changes
   * because of somebody ELSE — a reader objecting to a post — and nothing in the product corrects
   * it. A moderator who leaves the desk open and comes back an hour later is looking at what was
   * true an hour ago, and the panel draws NOTHING when the queue is empty, so an arrived report
   * looks exactly like the quiet afternoon they left.
   *
   * A gesture, not a timer: one request when somebody looks, none while they are away.
   */
  useEffect(() => {
    const cameBack = (): void => {
      if (document.visibilityState !== 'visible') return;
      readDesk();
    };
    document.addEventListener('visibilitychange', cameBack);
    return () => { document.removeEventListener('visibilitychange', cameBack); };
  }, [readDesk]);

  /*
   * ONE REMOVAL PER POST. `DELETE posts/:id` answers `no such post to remove` once it is gone, so a
   * moderator who presses twice is told the removal failed BECAUSE THE FIRST ONE WORKED — and
   * `acting` says every refusal out loud.
   *
   * KEEP beside it needs no guard: clearing the reports of a post that still exists succeeds every
   * time, so a second press answers what the first did. Guarding it would be a rule applied because
   * it exists rather than because it decides anything.
   */
  const row = useOneAtATime();

  if (!moderates(role)) return null;
  /* An empty queue with nobody suspended is the good state and the common one; a heading that
     permanently says "none reported" is furniture, and furniture about abuse is worse than most. */
  /* An empty queue is drawn as nothing, which is right — a moderator with no work should not be
     shown a panel about it. A queue we could not READ is not an empty one, and hiding it there is
     how a moderator comes to believe the site is quiet. */
  if (!unread && waiting === 0 && suspended.length === 0 && history.length === 0) return null;

  /*
   * WALK BACK THROUGH IT, which is what makes a record rather than a window.
   *
   * The same lesson the Hall log had to learn: the server answers a page at a time, and if the page
   * is everything a client can reach then a dispute raised after the hundredth act since is about a
   * row nothing can ask for. A moderator asked to justify a suspension from three weeks ago needs
   * to be able to produce it.
   *
   * The cursor is the OLDEST row already held, so "earlier" means everything before what is on
   * screen. An empty page is the end of the record and the control goes away — a button that does
   * nothing is worse than no button.
   */
  const earlier = async (): Promise<void> => {
    const oldest = history[history.length - 1];
    if (!oldest) return;
    let older;
    try {
      /* The ROW it stopped at — see the Hall log, which pages the same way for the same reason. */
      older = await client.siteLog({ before: oldest.at, beforeId: oldest.id });
    } catch {
      /* The same as the Hall record's, and for the same reason: a page that failed is not the
         end of the record, and calling it one takes away the control that could try again. */
      setUnread(true);
      return;
    }
    setHistory(current => [...current, ...older]);
    setHistoryEnded(older.length === 0);
  };

  /*
   * AND THE SUSPENDED LIST IS A PAGE TOO, which nothing said and which made the switch
   * irreversible past it.
   *
   * The server capped this list at 200 with no sort and no sign there was more of it, and the
   * reinstate control is drawn on a ROW of it — so a reader past the cap could not be reinstated
   * through the product by anybody, ever. That is precisely the state this list was written to
   * end: *"without it, undo would mean an operator editing the database again"*.
   *
   * The cursor is the ROW and only the row: the document records that an account is off and never
   * when it was turned off, so there is no instant to page by.
   */
  const moreSuspended = async (): Promise<void> => {
    const last = suspended[suspended.length - 1];
    if (!last) return;
    let older;
    try {
      older = await client.suspendedReaders({ beforeId: last.id });
    } catch {
      /* A page that FAILED is not the end of the list, and saying it is takes away the only
         control that could try again — the Hall record's lesson, one desk along. */
      setUnread(true);
      return;
    }
    setSuspended(current => [...current, ...older]);
    setSuspendedEnded(older.length === 0);
  };

  /* Acting on one removes its row here rather than re-reading the whole queue: a moderator working
     through a list should not have it shift under them between decisions. */
  /*
   * WHAT A PRESS DID, WHEN IT DID NOT DO IT.
   *
   * Every control here was `void client.something(...).then(...).catch(() => {})`. A moderator
   * pressing Remove on an abusive post, with the request failing, got NOTHING: no message, the row
   * still there, the post still up. They press it again, and again, and the only account of what
   * happened is in a console nobody has open.
   *
   * This is the emergency console — the one screen in the product where a press is somebody being
   * suspended or a post disappearing for everybody — so a silent press is worse here than anywhere
   * else. The same defect the microphone had and the Halls form had, on the screen with the least
   * room for it.
   *
   * The refusal's own words where the server wrote them, and the panel's phrase otherwise, which is
   * the rule `refusalWords` exists for; it also writes the unrecognised ones down, so a report of
   * "it did nothing" leaves somebody able to find out why.
   */
  /** Take a reported post down — one removal per post, and `acting` says any refusal. */
  const takeDown = async (postId: string): Promise<void> =>
    row.run(postId, () => acting(async () => { await client.deletePost(postId); done(postId); }));

  const acting = async (work: () => Promise<void>): Promise<void> => {
    setProblem(null);
    try {
      await work();
    } catch (caught) {
      setProblem(refusalWords(t, caught, 'reports.failed'));
    }
  };

  const done = (postId: string): void => {
    setPosts(current => current.filter(post => post.id !== postId));
    setWaiting(current => Math.max(current - 1, 0));
  };

  return (
    <section className="modq" aria-label={t('reports.heading')}>
      <button type="button" className="modq-toggle" onClick={() => { setOpen(!open); if (!open) void load(); }} aria-expanded={open}>
        {/* The count only when there is one to give. `(0)` beside a failed read is the claim this
            panel was making. */}
        {unread ? t('reports.heading') : `${t('reports.heading')} (${waiting})`}
      </button>
      {problem && <p className="modq-problem" role="alert">{problem}</p>}
      {unread && <p className="modq-problem" role="alert">{t('shared.couldNotRead')}</p>}
      {open && (
        <ul className="modq-list">
          {posts.map(post => (
            <li key={post.id} className="modq-row">
              <p className="modq-body">{post.body}</p>
              <p className="modq-meta">
                {/* The author's name, and WHERE it was written: the same words are a different act
                    on a passage about war than on one about hospitality. */}
                <span className="modq-who">{post.author ?? t('reports.departed')}</span>
                <button type="button" className="modq-where" onClick={() => onGoToNode(post.nodeId)}>
                  {nodeName(post.nodeId)}
                </button>
                <span className="modq-count">{t('reports.times', { count: String(post.reportCount) })}</span>
              </p>
              <button
                type="button"
                className="modq-keep"
                onClick={() => void acting(async () => { await client.clearReports(post.id); done(post.id); })}
              >
                {t('reports.keep')}
              </button>
              <button
                type="button"
                className="modq-remove"
                onClick={() => void takeDown(post.id)}
                disabled={row.working(post.id)}
              >
                {t('reports.remove')}
              </button>
              {/*
                AND THE PERSON, WHEN THE POST IS NOT THE PROBLEM. Offered here because this is the
                only place in the product that can name a reader — and it is the emergency control
                §6 describes, which until now lived in the database and nowhere else.

                Reversible, and the list below is how: an emergency control with no way back is one
                a moderator hesitates to use in the emergency it exists for. Absent for a post whose
                author has deleted their account — there is nobody left to suspend.
              */}
              {post.author !== null && (
                <button
                  type="button"
                  className="modq-suspend"
                  onClick={() => void acting(async () => {
                    await client.suspendReader(post.authorId);
                    setSuspended(current => [...current, { id: post.authorId, displayName: post.author ?? '' }]);
                    done(post.id);
                  })}
                >
                  {t('reports.suspend', { name: post.author })}
                </button>
              )}
            </li>
          ))}
          {/* A capped page says so rather than reading as the whole queue. */}
          {waiting > posts.length && <li className="modq-more">{t('reports.more', { count: String(waiting - posts.length) })}</li>}
        </ul>
      )}
      {/*
        WHO IS SUSPENDED — and the only way to find them. Their posts may be gone and they are in no
        room to be seen in, so without this list, lifting a suspension means editing the database:
        the exact state the control was built to replace.
      */}
      {open && suspended.length > 0 && (
        <ul className="modq-suspended">
          {suspended.map(reader => (
            <li key={reader.id} className="modq-suspended-row">
              <span className="modq-who">{reader.displayName}</span>
              <button
                type="button"
                className="modq-reinstate"
                onClick={() => void acting(async () => {
                  await client.reinstateReader(reader.id);
                  setSuspended(current => current.filter(one => one.id !== reader.id));
                })}
              >
                {t('reports.reinstate')}
              </button>
            </li>
          ))}
          {/* Drawn until a page comes back empty, like the record below it. A moderator who cannot
              reach a suspended reader cannot lift their suspension. */}
          {!suspendedEnded && (
            <li className="modq-more">
              <button type="button" className="modq-earlier" onClick={() => void moreSuspended()}>
                {t('reports.earlier')}
              </button>
            </li>
          )}
        </ul>
      )}
      {/*
        WHAT SITE MODERATORS HAVE DONE — the history behind the list above.
        The suspended list is one bit of state: who is locked out right now. It cannot say who
        locked them out, when, or how many times this reader has been through it, and those are the
        questions a second moderator has before deciding whether to lift it. Drawn only when there
        is something in it, like everything else in this panel.
      */}
      {open && history.length > 0 && (
        <ul className="modq-history" aria-label={t('reports.history')}>
          {history.map(entry => (
            <li key={entry.id} className="modq-history-row">
              <span className="modq-who">{entry.subject ?? t('reports.departed')}</span>
              <span className="modq-action">
                {t(siteSaidBy(entry.action))}
              </span>
              {/* By whom, which is the whole point of a record of delegated power. */}
              <span className="modq-actor">{entry.actor ?? t('reports.departed')}</span>
              {/* The same guard as the Hall record and the studies panel — see `readableTime`. */}
              <span className="modq-when">
                {readableTime(entry.at, lang, t('shared.timeUnknown'), {})}
              </span>
            </li>
          ))}
          {/* Offered whenever the end has not been reached, and never compared against the server's
              page size — that number is the server's, and a copy of it here would be a second one
              to keep in step. One click on a short record learns there are no more, and the control
              then goes away for good. */}
          {!historyEnded && (
            <li className="modq-history-row">
              <button type="button" className="modq-earlier" onClick={() => void earlier()}>
                {t('reports.earlier')}
              </button>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
